import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const companyNavItems = [
  { path: '/about', label: 'About ZhiSu' },
  { path: '/careers', label: 'Careers' },
  { path: '/contact', label: 'Contact' },
  { path: '/press', label: 'Press & Media' },
  { path: '/brand', label: 'Brand Guidelines' },
];

export default function BrandPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';

  return (
    <CompanyLayout
      title={t('brand.title', 'Brand Guidelines')}
      subtitle={t('brand.subtitle', 'Official brand assets, color palette, and usage guidelines for ZhiSu')}
      category="Company"
      navItems={companyNavItems}
    >
      {/* Logo */}
      <div className={`p-6 rounded-xl border ${borderLight} ${bgCard} mb-6`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('brand.logoUsage', 'Logo')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className={`p-6 rounded-lg border ${borderLight} flex items-center justify-center ${isDark ? 'bg-[#050505]' : 'bg-white'}`}>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg overflow-hidden border border-[#00ff88]/20">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20futuristic%20AI-themed%20crypto%20exchange%20logo%20featuring%20the%20letters%20A%20and%20I%20fused%20into%20a%20single%20elegant%20monogram%20with%20electric%20neon%20green%20gradient%20glow%20and%20neural%20network%20node%20connections%2C%20minimal%20geometric%20design%20on%20a%20deep%20black%20background%20with%20subtle%20digital%20grid%20texture%2C%20professional%20brand%20identity%2C%20high%20resolution%2C%20clean%20sharp%20edges%2C%20no%20additional%20text%20or%20elements%2C%20centered%20composition%2C%20perfect%20for%20dark%20themed%20trading%20platform%20website%20header%20and%20app%20icon&width=256&height=256&seq=logo_zhisu_ai_v1&orientation=squarish"
                  alt="ZhiSu"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-semibold text-lg tracking-tight text-white">ZhiSu</span>
            </div>
          </div>
          <div className={`p-6 rounded-lg border ${borderLight} flex items-center justify-center bg-white`}>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg overflow-hidden border border-[#00ff88]/20">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20futuristic%20AI-themed%20crypto%20exchange%20logo%20featuring%20the%20letters%20A%20and%20I%20fused%20into%20a%20single%20elegant%20monogram%20with%20electric%20neon%20green%20gradient%20glow%20and%20neural%20network%20node%20connections%2C%20minimal%20geometric%20design%20on%20a%20deep%20black%20background%20with%20subtle%20digital%20grid%20texture%2C%20professional%20brand%20identity%2C%20high%20resolution%2C%20clean%20sharp%20edges%2C%20no%20additional%20text%20or%20elements%2C%20centered%20composition%2C%20perfect%20for%20dark%20themed%20trading%20platform%20website%20header%20and%20app%20icon&width=256&height=256&seq=logo_zhisu_ai_v1&orientation=squarish"
                  alt="ZhiSu"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-semibold text-lg tracking-tight text-[#111]">ZhiSu</span>
            </div>
          </div>
        </div>
        <p className={`text-sm mt-4 ${textSecondary}`}>
          {t('brand.logoRules1', 'The ZhiSu logo consists of the V monogram and the wordmark. Always maintain clear space around the logo equal to the height of the V monogram.')}
        </p>
      </div>

      {/* Colors */}
      <div className={`p-6 rounded-xl border ${borderLight} ${bgCard} mb-6`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('brand.colorPalette', 'Color Palette')}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="h-16 rounded-lg bg-[#00ff88] mb-2" />
            <div className={`text-xs font-mono ${textMuted}`}>#00FF88</div>
            <div className={`text-xs ${textSecondary}`}>{t('brand.primary', 'Primary Neon')}</div>
          </div>
          <div>
            <div className="h-16 rounded-lg bg-[#050505] border border-white/10 mb-2" />
            <div className={`text-xs font-mono ${textMuted}`}>#050505</div>
            <div className={`text-xs ${textSecondary}`}>{t('brand.background', 'Dark Background')}</div>
          </div>
          <div>
            <div className="h-16 rounded-lg bg-[#f5f5f5] border border-black/10 mb-2" />
            <div className={`text-xs font-mono ${textMuted}`}>#F5F5F5</div>
            <div className={`text-xs ${textSecondary}`}>{t('brand.secondary', 'Light Background')}</div>
          </div>
          <div>
            <div className="h-16 rounded-lg bg-[#ff5566] mb-2" />
            <div className={`text-xs font-mono ${textMuted}`}>#FF5566</div>
            <div className={`text-xs ${textSecondary}`}>{t('brand.accent', 'Alert / Danger')}</div>
          </div>
        </div>
      </div>

      {/* Typography */}
      <div className={`p-6 rounded-xl border ${borderLight} ${bgCard} mb-6`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('brand.typography', 'Typography')}</h2>
        <div className="space-y-4">
          <div>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('brand.headingFont', 'Display / Headings')}</div>
            <div className={`text-2xl font-bold ${textPrimary}`}>Inter, Bold, 32px</div>
          </div>
          <div>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('brand.bodyFont', 'Body Text')}</div>
            <div className={`text-base ${textSecondary}`}>Inter, Regular, 16px</div>
          </div>
          <div>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('brand.monoFont', 'Monospace / Data')}</div>
            <div className={`text-base font-mono ${textSecondary}`}>JetBrains Mono, Regular, 14px</div>
          </div>
        </div>
      </div>

      {/* Usage Rules */}
      <div className={`p-6 rounded-xl border ${borderLight} ${bgCard}`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('brand.logoUsage', 'Usage Guidelines')}</h2>
        <ul className={`space-y-2 text-sm list-disc pl-5 ${textSecondary}`}>
          <li>Do not distort, rotate, or alter the logo proportions.</li>
          <li>Do not use the logo on backgrounds that compromise legibility.</li>
          <li>Always use the approved color palette for brand consistency.</li>
          <li>The wordmark &quot;ZhiSu&quot; should always appear with the Inter font.</li>
          <li>Maintain minimum clear space around the logo equal to the V monogram height.</li>
          <li>For questions about brand usage, contact <a href="mailto:brand@zhisu.trade" className="text-[#00ff88] hover:underline">brand@zhisu.trade</a>.</li>
        </ul>
      </div>
    </CompanyLayout>
  );
}